import type { ManipulateType } from 'dayjs'

import { TimeUnit } from 'generated/graphql'
import type { RangeQuery } from 'generated/graphql'
import type { TranslateOptions } from 'lib/tolgee'
import { PATHNAMES } from 'utils/routes/consts'
import TOLGEE_GLOBALS from 'utils/tolgeeGlobals'

import type { translations } from './winnin-lite.i18n'
import type { TMediaType, TPortfolioTrackingTierId } from './winnin-lite.types'
import type {
  TComponentsPermissionsOverride,
  TMenuPermissionsOverride,
  TModuleRulesOverride,
  TPermissionModule,
  TRouterPermission,
} from '../../winnin-auth-permissions/common/winnin-auth-permissions.types'

export const MODULE_NAME: TPermissionModule = 'winnin-lite'

export const MODULE_ROUTER_PERMISSIONS: TRouterPermission = {
  default: PATHNAMES.WORKSPACE_BRANDS_PORTFOLIO_TRACKING,
  routes: [
    PATHNAMES.WORKSPACE_BRANDS,
    PATHNAMES.WORKSPACE_BRANDS_PORTFOLIO_TRACKING,
    PATHNAMES.WORKSPACE_BRAND_OVERVIEW,
    PATHNAMES.WORKSPACE_VIDEO_MODAL,
    PATHNAMES.GOD_MODE,
    PATHNAMES.FLAGS,
  ],
}

export const MODULE_COMPONENTS_PERMISSIONS: TComponentsPermissionsOverride = {
  brandComponents: {
    providersChips: { hidden: true },
    zaiQuestions: { hidden: true },
    topicLinks: { hidden: true },
    videoSelect: { hidden: true },
    videoModalTopic: { hidden: true },
    brandTabNiches: { blocked: true },
    brandTabFormats: { blocked: true },
    brandTabAffinities: { blocked: true },
    brandTabAudience: { blocked: true },
    nichesCard: { blocked: true },
    creatorsCard: { blocked: true },
    providerFilterDropdown: { hidden: true },
    territoryAudienceFilter: { hidden: true },
    creatorLink: { hidden: true },
    creatorSelect: { hidden: true },
    zaiSideChatButton: { hidden: true },
    creatorsMonthFilter: { hidden: false },
    // Module-only opt-in (read via isBrandComponentModuleOnly): swaps the brand creators tab for the
    // tier-grouped one, which reads the backend's total-creators pool instead of the 6 partial
    // semantic clusters the default tab is built on.
    brandCreatorsByTier: { hidden: false },
    brandOwnerVisibilityToggle: { hidden: false },
    portfolioTrackingExport: { hidden: false },
  },
}

export const MODULE_RULES: TModuleRulesOverride = {
  restrictUploadDateToCurrentYear: true,
}

export const MODULE_MENU_PERMISSIONS: TMenuPermissionsOverride = {
  home: { hidden: false, disabled: true },
  soa: { hidden: false, disabled: true },
  tags: { hidden: false, disabled: true },
  topics: { hidden: false, disabled: true },
  videos: { hidden: false, disabled: true },
  creators: { hidden: false, disabled: true },
  industry: { hidden: false, disabled: true },
  dashboards: { hidden: false, disabled: true },
  reports: { hidden: false, disabled: true },
  compare: { hidden: false, disabled: true },
  zai: { hidden: false, disabled: true },
  liveEvents: { hidden: false, disabled: true },
  trends: { hidden: false, disabled: true },
}

export const BRAND_PAGE_PATHS = [
  PATHNAMES.WORKSPACE_BRAND_OVERVIEW,
  PATHNAMES.WORKSPACE_BRAND,
  PATHNAMES.WORKSPACE_BRANDS,
]

export const PERIOD_LABEL_FORMAT = 'MMM D'

export const MEDIA_TYPES: TMediaType[] = ['owned', 'earned', 'paid']

export const MEDIA_TYPE_TRANSLATIONS: Record<TMediaType, TranslateOptions> = {
  owned: TOLGEE_GLOBALS.owned,
  earned: TOLGEE_GLOBALS.earned,
  paid: TOLGEE_GLOBALS.paid,
}

// Highcharts turns series borderColor into an SVG `stroke` attribute and parses textOutline into
// one too, and var(--token) is not valid attribute syntax — so these two need literals mirroring
// --color_neutral_100 / --color_neutral_0. Everything it applies as real CSS (dataLabels `color`,
// useHTML markup) renders inside <ThemeProvider> and uses var(--color_*) directly.
export const CHART_COLUMN_BORDER_COLOR = '#f7f9fc'
export const CHART_SLICE_BORDER_COLOR = '#ffffff'
export const CHART_LABEL_OUTLINE_COLOR = '#000000'

export const MEDIA_TYPE_COLORS: Record<TMediaType, string> = {
  earned: '#36169c',
  paid: '#755cff',
  owned: '#d5d4ff',
}

export const BRAND_COLORS: string[] = ['#016622', '#23225b', '#00abd7', '#345ba7', '#f2c92f', '#e3641e', '#9b1e6d']

export const WEEK_RANGE_SEPARATOR = ' - '

export const BASE_X_AXIS_OPTIONS = { tickInterval: 1 }

// Bounds the total-creators POOL the backend builds; TIER_SUBSCRIBER_RANGES below slices that pool per
// tier. `{ gte: 0 }` matches the backend default — sent explicitly so a server-side change to that
// default can't silently redefine the pool.
export const VARIETY_POOL_SUBSCRIBER_RANGE: RangeQuery = { gte: 0 }

// Inclusive bounds only, and disjoint: the tiers are summed into a total and rendered as percentages,
// so a creator matching two tiers would be counted twice and the bars would exceed 100%. The upstream
// also rewrites gt/lt into gte/lte, so a `gt` here would filter on a different boundary than it reads.
// A partition over integers — 9_999.5 belongs to no tier.
export const TIER_SUBSCRIBER_RANGES: Record<TPortfolioTrackingTierId, RangeQuery> = {
  mega: { gte: 1_000_000 },
  macro: { gte: 100_000, lte: 999_999 },
  mid: { gte: 50_000, lte: 99_999 },
  micro: { gte: 10_000, lte: 49_999 },
  nano: { gte: 0, lte: 9_999 },
}

export const TIER_IDS: TPortfolioTrackingTierId[] = ['mega', 'macro', 'mid', 'micro', 'nano']

export const TIER_NAME_TRANSLATION_KEYS: Record<TPortfolioTrackingTierId, keyof typeof translations> = {
  mega: 'megaName',
  macro: 'macroName',
  mid: 'midTierName',
  micro: 'microName',
  nano: 'nanoName',
}

// Describe TIER_SUBSCRIBER_RANGES in words, for the surfaces that label a tier section rather than a
// chart axis. Keep in sync with the ranges above.
export const TIER_DESCRIPTION_TRANSLATION_KEYS: Record<TPortfolioTrackingTierId, keyof typeof translations> = {
  mega: 'megaDescription',
  macro: 'macroDescription',
  mid: 'midTierDescription',
  micro: 'microDescription',
  nano: 'nanoDescription',
}

export const TIME_UNIT_TO_DAYJS_UNIT: Partial<Record<TimeUnit, ManipulateType>> = {
  [TimeUnit.Hour]: 'hour',
  [TimeUnit.Day]: 'day',
  [TimeUnit.Week]: 'week',
  [TimeUnit.Month]: 'month',
  [TimeUnit.Year]: 'year',
}
